import { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { toast } from "sonner";

const STATUSES = ["confirmed", "packed", "shipped", "delivered", "cancelled"];
const STATUS_COLOR = {
  confirmed: "border-[#FF6A00] text-[#FF6A00]",
  packed: "border-yellow-500 text-yellow-500",
  shipped: "border-blue-400 text-blue-400",
  delivered: "border-green-500 text-green-500",
  cancelled: "border-[#E5E1D8] text-[#6E6E6E]",
};

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = () => adminApi.get("/bookings").then((r) => setBookings(r.data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (booking_id, status) => {
    try {
      await adminApi.put(`/admin/bookings/${booking_id}/status`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const visible = useMemo(() => {
    let list = bookings;
    if (statusFilter !== "all") list = list.filter((b) => b.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((b) =>
        b.booking_id.toLowerCase().includes(q) ||
        (b.user_email || "").toLowerCase().includes(q) ||
        (b.user_name || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [bookings, statusFilter, search]);

  const totalRevenue = bookings.reduce((s, b) => s + (b.total || 0), 0);

  return (
    <AdminShell>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="brand-tag">// Bookings</div>
          <h1 className="font-display text-5xl uppercase tracking-tight mt-1">Orders</h1>
        </div>
        <div className="border border-[#E5E1D8] bg-[#FFFFFF] px-4 py-2">
          <div className="font-mono-ds text-[9px] uppercase tracking-[0.3em] text-[#6E6E6E]">Total Revenue</div>
          <div className="font-display text-2xl text-[#FF6A00]">₹{totalRevenue.toLocaleString("en-IN")}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex border border-[#E5E1D8] bg-[#FFFFFF] p-1" data-testid="booking-status-filter">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`status-filter-${s}`}
              className={`px-3 h-8 font-mono-ds text-[10px] uppercase tracking-widest ${
                statusFilter === s ? "bg-[#FF6A00] text-[#161616] font-bold" : "text-[#6E6E6E] hover:text-[#161616]"
              }`}
            >
              {s}
              <span className="ml-1.5 opacity-60">
                {s === "all" ? bookings.length : bookings.filter((b) => b.status === s).length}
              </span>
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by ID / email / name"
          className="bg-transparent border border-[#E5E1D8] px-3 h-9 font-mono-ds text-xs focus:border-[#FF6A00] outline-none min-w-[260px]"
          data-testid="booking-search-input"
        />
      </div>

      <div className="border border-[#E5E1D8] bg-[#FFFFFF] overflow-x-auto">
        <table className="w-full text-sm font-mono-ds min-w-[1100px]">
          <thead className="border-b border-[#E5E1D8] text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">
            <tr>
              <th className="text-left p-4">Booking</th>
              <th className="text-left p-4">Customer</th>
              <th className="text-left p-4">Live</th>
              <th className="text-left p-4">Qty</th>
              <th className="text-left p-4">Total</th>
              <th className="text-left p-4">Ships To</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">When</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-[#6E6E6E]">// No bookings match this filter</td></tr>
            )}
            {visible.map((b) => (
              <tr key={b.booking_id} className="border-b border-[#FFFFFF] hover:bg-[#FFFFFF]/70" data-testid={`booking-row-${b.booking_id}`}>
                <td className="p-4 text-[#161616]">{b.booking_id}</td>
                <td className="p-4 text-[#6E6E6E]">
                  {b.user_name || "—"}
                  <span className="text-[#6E6E6E] text-[10px] block">{b.user_email}</span>
                </td>
                <td className="p-4 text-[#6E6E6E] text-[10px]">{b.live_id}</td>
                <td className="p-4 text-[#161616]">{b.quantity}</td>
                <td className="p-4 text-[#FF6A00] font-bold">₹{b.total}</td>
                <td className="p-4 text-[#6E6E6E]">
                  {b.shipping_address ? (
                    <div className="leading-tight">
                      <div className="text-[#161616] text-xs truncate max-w-[200px]">{b.shipping_address.line1}</div>
                      <div className="text-[10px] text-[#6E6E6E]">
                        {b.shipping_address.city}, {b.shipping_address.state} {b.shipping_address.postal_code}
                      </div>
                      {b.shipping_address.phone && (
                        <div className="text-[10px] text-[#6E6E6E]">📞 {b.shipping_address.phone}</div>
                      )}
                    </div>
                  ) : <span className="text-[#6E6E6E]">—</span>}
                </td>
                <td className="p-4">
                  <select
                    value={b.status}
                    onChange={(e) => updateStatus(b.booking_id, e.target.value)}
                    data-testid={`booking-status-select-${b.booking_id}`}
                    className={`bg-transparent border px-2 py-1 font-mono-ds text-[10px] uppercase tracking-widest focus:border-[#FF6A00] outline-none ${STATUS_COLOR[b.status] || "border-[#E5E1D8] text-[#6E6E6E]"}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s} className="bg-[#FFFFFF] text-[#161616]">{s}</option>)}
                  </select>
                </td>
                <td className="p-4 text-[#6E6E6E] text-[10px]">{new Date(b.created_at).toLocaleString("en-IN")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminShell>
  );
}
